import { useCallback } from 'react';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { redirect } from '@/navigation';
import { useWregAppLink } from '@/components/webreg/useWregAppLink';
import { WregIntake } from '@/components/webreg/screens/WregIntake';
import { WregScreener } from '@/components/webreg/screens/WregScreener';
import { WregTracks } from '@/components/webreg/screens/WregTracks';
import { WregAgreement } from '@/components/webreg/screens/WregAgreement';
import { WregSwitch } from '@/components/webreg/screens/WregSwitch';
import { WregLabs } from '@/components/webreg/screens/WregLabs';
import { WregMonitor } from '@/components/webreg/screens/WregMonitor';
import { WregAllSet } from '@/components/webreg/screens/WregAllSet';
import { useWregFlowActions } from '@/components/webreg/WregFlowProvider';
import { useWregDev } from '@/components/webreg/dev/WregDevContext';
import { wregShippingFromContext } from '@/components/webreg/wregFlowState';
import type { WregScreenEvent } from '@/components/webreg/screens/wregScreenEvents';

const useScreenEvent = (beacon?: boolean): WregScreenEvent => {
  const { track } = useAnalytics();
  return useCallback(
    (event, properties) => track({ event, properties, beacon }),
    [beacon, track],
  );
};

const WregIntakeRoute = () => {
  const { form, intakeNotice, runIntake, afterIntake } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregIntake
      notice={intakeNotice}
      initial={form}
      onSubmit={runIntake}
      onSubmitted={afterIntake}
      onEvent={onEvent}
    />
  );
};

const WregScreenerRoute = () => {
  const { onScreenerContinue, onScreenerIneligible } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregScreener
      onContinue={onScreenerContinue}
      onIneligible={onScreenerIneligible}
      onEvent={onEvent}
    />
  );
};

const WregTracksRoute = () => {
  const { elig, tracks, tracksGate, onTracksContinue, onTracksNoCondition } = useWregFlowActions();
  const { disclosure } = useWregDev();
  return (
    <WregTracks
      offered={elig.tracks}
      blocked={elig.blocked}
      gate={tracksGate}
      disclosure={disclosure}
      initial={tracks}
      onContinue={onTracksContinue}
      onNoCondition={onTracksNoCondition}
    />
  );
};

const WregAgreementRoute = () => {
  const { runAlign, afterAlign, onDecline } = useWregFlowActions();
  return <WregAgreement onAccept={runAlign} onAccepted={afterAlign} onDecline={onDecline} />;
};

const WregSwitchRoute = () => {
  const { tracks, onSwitchAttest, onDecline } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregSwitch tracks={tracks} onSwitch={onSwitchAttest} onDecline={onDecline} onEvent={onEvent} />
  );
};

const WregLabsRoute = () => {
  const { registrationData } = useRegistration();
  const { onLabsYes, onLabsNo } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregLabs
      initial={registrationData.labsCompleted}
      onYes={onLabsYes}
      onNo={onLabsNo}
      onEvent={onEvent}
    />
  );
};

const WregMonitorRoute = () => {
  const { registrationData } = useRegistration();
  const { saveShipping, onShippingContinued } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregMonitor
      initial={wregShippingFromContext(registrationData)}
      onContinue={saveShipping}
      onContinued={onShippingContinued}
      onEvent={onEvent}
    />
  );
};

const WregAllSetRoute = () => {
  const getAppLink = useWregAppLink();
  const onEvent = useScreenEvent(true);
  return <WregAllSet onEnter={() => redirect(getAppLink())} onEvent={onEvent} />;
};

export {
  WregAgreementRoute,
  WregAllSetRoute,
  WregIntakeRoute,
  WregLabsRoute,
  WregMonitorRoute,
  WregScreenerRoute,
  WregSwitchRoute,
  WregTracksRoute,
};
